// HISBedStateDR.js
// [FIX] alertData 加入 nextDay，傳給 BedStateRow 的 store_alerts 合併 current+nextDay，
//       確保動態時間軸（08:00～隔日08:00）能顯示隔日凌晨資料。

import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ApiGo from '../../../../actions/ApiGo';
import { LogTime } from '../../../../actions/forFrontEnd_Log';
import { ADD_HIS_ALERTS, ADD_HIS_MOBILITY } from '../../../../stores/reducer_HistoryData';
import UiIntegrater from '../littleUIComponent/UiIntegrater';
import BedStateRow from './BedStateRow';
import { ADD_RESPONSE, ADD_RESPONSE_JSON } from '../../../../stores/reducer_add';
import BedStateRowThreeDay from './BedStateRowThreeDay';

const HISBedStateDR = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = props.data;
  const [prevAlertsForThreeDay, setPrevAlertsForThreeDay] = useState([]);
  const [chartSwitch, setChartSwitch] = useState(0);
  const [returnShift, setReturnShift] = useState('ALL');
  const [returnState, setReturnState] = useState('all');
  const [isLoading, setIsLoading]     = useState(true);

  // [FIX] 加入 nextDay
  const [alertData, setAlertData] = useState({
    current:    [],
    yesterday:  [],
    twoDaysAgo: [],
    nextDay:    [],   // ← 新增
    nextNextDay: [],  // 補上這行
  });
  const [mobilityData, setMobilityData] = useState({
    current:    [],
    yesterday:  [],
    twoDaysAgo: [],
    nextDay:    [],
    nextNextDay: [],  // 新增
  });
  
  const [prevAlertsForChart, setPrevAlertsForChart] = useState([]);  // ✅ 新增
  const [nextDayWas404, setNextDayWas404] = useState(false);
  const store_timestamp = useSelector((state) => state.store_historyData.timestamp);

  // ✅
  const date = useMemo(() => {
      if (!store_timestamp || String(store_timestamp).length === 0) return '';
      return new LogTime(new Date(store_timestamp)).getDate();
  }, [store_timestamp]);

  // ✅ 直接用 UTC+8 算，完全不碰 local time
  const calculateDates = (timestamp) => {
      const toDateStr = (ts) => new LogTime(new Date(ts)).getDate();
      return {
          current:     toDateStr(timestamp),
          nextDay:     toDateStr(timestamp + 86400000),
          nextNextDay: toDateStr(timestamp + 172800000),
          yesterday:   toDateStr(timestamp - 86400000),
          twoDaysAgo:  toDateStr(timestamp - 172800000),
      };
  };
  
  const isFutureDate = (dateStr) => {
      if (!dateStr) return false;
      const parseMs = (d) => new Date(String(d) + 'T00:00:00+08:00').getTime();
      // 直接用 UTC+8 計算今天日期，完全不碰 local time
      const todayUTC8 = new LogTime(new Date()).getDate();
      return parseMs(dateStr) > parseMs(todayUTC8);
  };

  const handleApiError = (error) => {
    dispatch(ADD_RESPONSE({ status: error.status, statusText: error.statusText, type: 'hisRec_no_data' }));
    dispatch(ADD_HIS_ALERTS([]));
    return [];
  };
  
  const fetchData = async (dates) => {
    setIsLoading(true);
    dispatch(ADD_HIS_ALERTS([]));

    try {
      const fetchPromises = [
          ApiGo.getAlert(id, dates.current,    'ALL'),           // 1
          ApiGo.getAlert(id, dates.yesterday,  'ALL'),           // 2
          ApiGo.getAlert(id, dates.twoDaysAgo, 'ALL'),           // 3
          isFutureDate(dates.nextDay)
              ? Promise.resolve({ alerts: [] })
              : ApiGo.getAlert(id, dates.nextDay, 'ALL'),        // 4
          isFutureDate(dates.nextNextDay)                        // ✅ 移到這裡（第 5 個）
              ? Promise.resolve({ alerts: [] })
              : ApiGo.getAlert(id, dates.nextNextDay, 'ALL'),
          ApiGo.getMobility_inbed(id, dates.current),            // 6
          ApiGo.getMobility_inbed(id, dates.yesterday),          // 7
          ApiGo.getMobility_inbed(id, dates.twoDaysAgo),         // 8
          isFutureDate(dates.nextDay)
              ? Promise.resolve({ time_interval_count: [] })
              : ApiGo.getMobility_inbed(id, dates.nextDay),      // 9
      ];

      const [
        currentAlert,
        yesterdayAlert,
        twoDaysAlert,
        nextDayAlert,
        nextNextDayAlert,   // 新增
        currentMobility,
        yesterdayMobility,
        twoDaysMobility,
        nextDayMobility,   // ← 新增
      ] = await Promise.allSettled(fetchPromises);

      const safeAlerts   = (r) => (r.status === 'fulfilled' ? (r.value?.alerts  || []) : []);
      const safeMobility = (r) => (r.status === 'fulfilled' ? (r.value?.time_interval_count || []) : []);
      const nextDayEffectivelyEmpty =
        nextDayAlert.status === 'rejected' ||
        (isFutureDate(dates.nextDay) && safeAlerts(nextDayAlert).length === 0);

      const nextNextDayEffectivelyEmpty =
        nextNextDayAlert.status === 'rejected' ||
        (isFutureDate(dates.nextNextDay) && safeAlerts(nextNextDayAlert).length === 0);

      let resolvedNextDayWas404 = nextDayEffectivelyEmpty && nextNextDayEffectivelyEmpty;

      // 如果前兩天都空，再往後最多查 12 天，確認是否真的沒有後續資料
      if (resolvedNextDayWas404) {
          for (let i = 3; i <= 14; i++) {
              const futureDate = new Date(Number(store_timestamp) + i * 86400000 + 8 * 3600000)
                  .toISOString()
                  .slice(0, 10);
              if (isFutureDate(futureDate)) break;  // 未來日期就停止
              try {
                  const res = await ApiGo.getAlert(id, futureDate, 'ALL');
                  if (res?.alerts?.length > 0) {
                      resolvedNextDayWas404 = false;  // 有後續資料，不是最後一天
                      break;
                  }
              } catch (e) {
                  // 404 代表無資料，繼續找
              }
          }
      }

      setNextDayWas404(resolvedNextDayWas404);
      
      const newAlertData = {
        current:    safeAlerts(currentAlert),
        yesterday:  safeAlerts(yesterdayAlert),
        twoDaysAgo: safeAlerts(twoDaysAlert),
        nextDay:    safeAlerts(nextDayAlert),   // ← 新增
        nextNextDay: safeAlerts(nextNextDayAlert),  // 新增
      };

      const newMobilityData = {
        current:    safeMobility(currentMobility),
        yesterday:  safeMobility(yesterdayMobility),
        twoDaysAgo: safeMobility(twoDaysMobility),
        nextDay:    safeMobility(nextDayMobility),  // ← 新增
      };

      setAlertData(newAlertData);
      setMobilityData(newMobilityData);

      // 找 prevAlertsForChart（一天視圖用）
      let prevAlerts = [];
      if (newAlertData.yesterday.length > 0) {
        prevAlerts = newAlertData.yesterday;
      } else if (newAlertData.twoDaysAgo.length > 0) {
        prevAlerts = newAlertData.twoDaysAgo;
      } else {
        for (let i = 3; i <= 14; i++) {
          const farDate = new Date((store_timestamp - i * 86400000) + 8 * 3600000)
              .toISOString().slice(0, 10);
          try {
            const res = await ApiGo.getAlert(id, farDate, 'ALL');
            if (res?.alerts?.length > 0) { prevAlerts = res.alerts; break; }
          } catch (e) {}
        }
      }
      setPrevAlertsForChart(prevAlerts);

      // ✅ 找「兩天前之前」最近有資料的天（給兩天前 row 的 lastBeforeStart 用）
      // 必須從 day-3 開始，不能用 twoDaysAgo（已有 alertsTommorrow 重複）
      let prevForThreeDay = [];
      for (let i = 3; i <= 14; i++) {
        const farDate = new Date((store_timestamp - i * 86400000) + 8 * 3600000)
            .toISOString().slice(0, 10);
        try {
          const res = await ApiGo.getAlert(id, farDate, 'ALL');
          if (res?.alerts?.length > 0) {
            prevForThreeDay = res.alerts;
            break;
          }
        } catch (e) {}
      }
      setPrevAlertsForThreeDay(prevForThreeDay);


      // Redux 仍保存 current+nextDay 合併（供 HISBedState 等其他元件使用）
      const combinedForRedux = [...newAlertData.current, ...newAlertData.nextDay];
      dispatch(ADD_HIS_ALERTS(combinedForRedux));

      dispatch(ADD_RESPONSE_JSON({
        date:          dates.current,
        nextDay:       dates.nextDay,
        currentCount:  newAlertData.current.length,
        nextDayCount:  newAlertData.nextDay.length,
        combinedCount: combinedForRedux.length,
      }));
    } catch (error) {
      handleApiError(error);
      setAlertData({ current: [], yesterday: [], twoDaysAgo: [], nextDay: [], nextNextDay: [] });
      setMobilityData({ current: [], yesterday: [], twoDaysAgo: [] });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (store_timestamp && String(store_timestamp).length !== 0) {
      fetchData(calculateDates(store_timestamp));
    }
  }, [store_timestamp, returnShift]);

  // [FIX] BedStateRow 接收 current+nextDay 合併，確保隔日凌晨資料顯示在時間軸
  const combinedAlerts = useMemo(
      () => [...alertData.current, ...alertData.nextDay, ...alertData.nextNextDay],
      [alertData.current, alertData.nextDay, alertData.nextNextDay]
  );

  return (
    <>
      <UiIntegrater
        OnOff_switch={[true, false, false, true, true]}
        setReturnShift={setReturnShift}
        setReturnState={setReturnState}
        name={props.data !== undefined ? props.data.last_name  : ''}
        bed={props.data  !== undefined ? props.data.first_name : ''}
      />
      <div className="fx-d-c h-90">
        <div className="w-80 h-10 font-l fx-d-c addSwitch_SE inputBox_SE">
          <div className="fx-d-r w-100 h-100 fx-ccc SwitchTag blue">
            <div className={`fx-ccc outer_active l ${chartSwitch === 0 ? 'dismiss' : ''}`} onClick={() => setChartSwitch(0)}>
              <div className={`fx-ccc l ${chartSwitch === 0 ? 'inner_active blue' : ''}`}>{t('GLOBAL.UNIT.OneDay')}</div>
            </div>
            <div className={`fx-ccc outer_active r add ${chartSwitch === 2 ? '' : 'dismiss'}`} onClick={() => setChartSwitch(2)}>
              <div className={`fx-ccc r ${chartSwitch === 2 ? 'inner_active blue' : ''}`}>{t('GLOBAL.UNIT.ThreeDay')}</div>
            </div>
          </div>
        </div>

        {!isLoading && date && (chartSwitch === 0
          ? <BedStateRow
              key={`${store_timestamp}-${returnShift}`}
              date={date}
              store_alerts={combinedAlerts}   // ← current + nextDay
              shift={returnShift}
              state={returnState}
              mobilityData={mobilityData.current}
              prevDayAlerts={prevAlertsForChart}   // ✅ 加這行，資料已有現成的
              isLastActiveDay={nextDayWas404}
            />
          : <BedStateRowThreeDay
              key={`3d-${store_timestamp}-${returnShift}`}
              date={date}
              store_alerts={alertData.current}
              alertsYesterday={alertData.yesterday}
              alertsTommorrow={alertData.twoDaysAgo}
              alertsNextDay={alertData.nextDay}          // ← 新增
              alertsNextNextDay={alertData.nextNextDay}  // ✅ 新增
              mobilityData={mobilityData.current}
              mobilityDataYesterday={mobilityData.yesterday}
              mobilityDataTommorrow={mobilityData.twoDaysAgo}
              mobilityDataNextDay={mobilityData.nextDay} // ← 新增
              isLastActiveDayCurrent={nextDayWas404}
              isLastActiveDayYesterday={alertData.current.length === 0 && nextDayWas404}
              isLastActiveDayTwoDaysAgo={alertData.yesterday.length === 0 && alertData.current.length === 0 && nextDayWas404}
              prevAlertsForThreeDay={prevAlertsForThreeDay}
              shift={returnShift}
              state={returnState}
            />
        )}
      </div>
    </>
  );
};

export default HISBedStateDR;